import React, { useState } from 'react';
import {
  RailsItem,
  RailsThumbnailItem,
} from '@sls-react/typings/services/rails/item';
import { AnalyticsSource } from '@sls-react/services/analytics/AnalyticsInterfaces';
import FavoriteIcon from '@sls-react/static/svg/FavoriteIcon';
import useItemFavorite from '@sls-react/hooks/useItemFavorite';
import ItemDetailsLink from '@sls-react/components/Links/ItemDetailsLink';
import { RailsFacetItemMeta } from '@sls-react/typings/services/rails/facets';
import { useQueryClient } from 'react-query';
import ItemLabelPill from '@sls-react/components/ItemDetails/ItemLabelPill';
import UsernameAvatarRow from '@sls-react/components/ItemThumbnail/UsernameAvatarRow';
import ItemConditionGrade from '@sls-react/components/ItemThumbnail/ItemConditionGrade';
import HorizontalDots from '@sls-react/components/Icons/HorizontalDots';
import ItemThumbnailOverlay from '@sls-react/components/ItemThumbnail/ActionsOverlay/ItemThumbnailOverlay';
import useHasFeature from '@sls-react/hooks/useHasFeature';
import dynamic from 'next/dynamic';
import { useSession } from '@sls-react/contexts/SessionContext';
import usePrice from '@sls-react/hooks/usePrice';
import SuperScriptedCurrency from '@sls-react/components/ItemThumbnail/SuperScriptedCurrency';
import { useRouter } from '@sls-react/routing';
import classNames from 'classnames';
import ItemThumbnailImage from '@sls-react/components/ItemThumbnail/ItemThumbnailImage';
import ItemThumbnailShippingInfo from '@sls-react/components/ItemThumbnail/ItemThumbnailShippingInfo';

// TEMPORARY
const auctionRe = /auction/i;

const GearExpertItemThumbnailAction = dynamic(
  () =>
    import(
      '@sls-react/components/GearExperts/Lists/GearExpertItemThumbnailAction'
    ),
);

interface Props {
  // index of iterating through thumbnails in the parent grid component. starts with 0
  position?: number;
  item:
    | RailsThumbnailItem
    | (RailsThumbnailItem & RailsFacetItemMeta)
    | RailsItem;

  onInvalidate?: () => void;

  additionalLowerSection?: React.ReactNode;
  src?: AnalyticsSource;
}

const ItemThumbnail: React.FC<Props> = ({
  additionalLowerSection,
  item,
  onInvalidate,
  position,
  src,
}) => {
  const { user } = useSession();
  const { locale } = useRouter();
  const calculatePrice = usePrice({ decimals: false });
  const isGearExpert = useHasFeature('gear_expert');
  const [overlayOpen, setOverlayOpen] = useState(false);

  const price = calculatePrice(item.price);

  const queryClient = useQueryClient();

  const data = queryClient.getQueryData<RailsFacetItemMeta[]>('item-meta', {
    exact: false,
    active: true,
    predicate: q => (q.queryKey as [string, number[]])?.[1]?.includes(item.id),
  });

  const meta = data?.find(meta => meta.id === item.id);

  const itemNameCx = 'mb-2 text-sm text-black line-clamp-2';
  const itemNameLink = overlayOpen ? (
    <span className={itemNameCx}>{item.name}</span>
  ) : (
    <ItemDetailsLink
      className={classNames(
        itemNameCx,
        'hover:underline',
        !additionalLowerSection && 'thumbnail-link',
      )}
      item={item}
      title={item.name}
      src={src}
    >
      {item.name}
    </ItemDetailsLink>
  );

  const qualifiesForActionOverlay = !!(
    meta &&
    (meta.actions.find(
      a =>
        a === 'edit' ||
        a === 'take_down' ||
        a === 'remove_gmc' ||
        a === 'unreport',
    ) ||
      user?.username === item.seller.username)
  );

  let shouldShowBeforePrice = false;
  if (item.label && auctionRe.test(item.label.text)) {
    shouldShowBeforePrice = false;
  } else {
    if (
      (item.price_before || item.list_price) != item.price ||
      locale !== 'en-US'
    ) {
      shouldShowBeforePrice = true;
    }
  }

  const beforePrice =
    shouldShowBeforePrice &&
    calculatePrice(item.price_before || item.list_price);

  let retailPrice: React.ReactNode;
  if (item.price_retail > 0) {
    const retail = calculatePrice(item.price_retail);
    retailPrice = (
      <span className="text-slate-green-500 self-center ml-auto text-xs">
        Retail: {retail.formattedPrice}
        {retail.nonUSDCurrency && (
          <SuperScriptedCurrency>{retail.nonUSDCurrency}</SuperScriptedCurrency>
        )}
      </span>
    );
  }

  const { favorite, favorited, favoritersCount } = useItemFavorite(item, {
    meta,
  });

  return (
    <div className="thumbnail">
      {qualifiesForActionOverlay && (
        <ItemThumbnailOverlay
          item={item}
          meta={meta}
          onClose={() => setOverlayOpen(false)}
          onInvalidate={onInvalidate}
          show={overlayOpen}
        />
      )}
      {/* Image Container */}
      <div className="thumbnail-image aspect-w-1 aspect-h-1">
        <ItemThumbnailImage item={item} position={position} />

        {/* Container to properly position image container content */}
        <div className="flex flex-col justify-between p-2">
          {/* Label and Favorite row */}
          <div className="relative z-10 flex justify-between">
            {item.label ? (
              <ItemLabelPill
                label={item.label}
                auctionEndedAt={meta?.auction_ended_at}
              />
            ) : (
              <div />
            )}

            {/* Favorite button */}
            <button
              className="relative flex items-center -m-2 p-2 text-white"
              onClick={e =>
                item.seller.username === user?.username ? null : favorite(e)
              }
            >
              <FavoriteIcon fill="currentColor" active={favorited} />
              {favoritersCount > 0 && (
                <div className="ml-1 font-semibold">{favoritersCount}</div>
              )}
            </button>
          </div>

          {/* Username and action row */}
          <div className="relative z-10 flex items-center justify-between h-6">
            <UsernameAvatarRow item={item} />

            <div className="ml-auto" />

            {isGearExpert && (
              <GearExpertItemThumbnailAction
                item={item}
                className="mr-2 text-white"
              />
            )}

            {qualifiesForActionOverlay && (
              <button
                className="-m-2 p-2 text-white"
                onClick={() => setOverlayOpen(true)}
              >
                <HorizontalDots />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lower details container */}
      <div className="flex flex-col p-2 h-full">
        {itemNameLink}

        {/* I think this is where we want the whitespace expansion, in the event that multiple items on the same
            row have different heights. */}
        <div className="flex-1" />

        {/* Emblem and condition row */}
        <div className="flex items-center mb-2 space-x-2">
          <div>{meta && <ItemThumbnailShippingInfo meta={meta} />}</div>

          <div className="flex-1 text-right">
            <ItemConditionGrade item={item} />
          </div>
        </div>

        {/* Price row */}
        <div className="flex space-x-2">
          <span
            className={classNames('font-semibold', {
              // @ts-ignore
              'line-through': item.sold_via_offer,
            })}
          >
            {price.formattedPrice}
            {price.nonUSDCurrency && (
              <SuperScriptedCurrency>
                {price.nonUSDCurrency}
              </SuperScriptedCurrency>
            )}
          </span>

          {beforePrice && (
            <span className="text-slate-green-500 line-through">
              {beforePrice.formattedPrice}
              {beforePrice.nonUSDCurrency && (
                <SuperScriptedCurrency>
                  {beforePrice.nonUSDCurrency}
                </SuperScriptedCurrency>
              )}
            </span>
          )}

          {shouldShowBeforePrice ? null : (
            <>
              <div className="flex-1" />
              {retailPrice}
            </>
          )}
        </div>

        {shouldShowBeforePrice ? <div>{retailPrice}</div> : null}

        {additionalLowerSection}
      </div>
    </div>
  );
};

export default ItemThumbnail;
